import type { InvitedPerson } from '../types'
import { weddingInfo } from '../weddingInfo'
import { RunningOrder } from './RunningOrder'
import { SectionHeading } from './SectionHeading'

interface WeddingDetailsProps {
  /** Used to tailor the running order to what this invite actually covers. */
  people: readonly InvitedPerson[]
}

/** Everything about the day itself. All content comes from weddingInfo.ts. */
export function WeddingDetails({ people }: WeddingDetailsProps) {
  return (
    <div className="space-y-12">
      <section aria-labelledby="where-when">
        <SectionHeading id="where-when">Where and when</SectionHeading>
        <p className="mt-3">{weddingInfo.date}</p>
        <p className="font-display mt-1 text-xl">{weddingInfo.venue.name}</p>
        <p className="text-ink-soft mt-1 text-sm">{weddingInfo.venue.address}</p>
        {weddingInfo.venue.mapsUrl && (
          <a
            href={weddingInfo.venue.mapsUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="text-claret mt-3 inline-block text-sm underline decoration-1 underline-offset-4"
          >
            Open in Google Maps
          </a>
        )}
      </section>

      <RunningOrder people={people} />

      {weddingInfo.sections.map((section) => (
        <section key={section.heading} aria-labelledby={slug(section.heading)}>
          <SectionHeading id={slug(section.heading)}>{section.heading}</SectionHeading>
          <div className="mt-3 space-y-3">
            {section.body.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
        </section>
      ))}

      <section aria-labelledby="questions">
        <SectionHeading id="questions">Questions</SectionHeading>
        <dl className="mt-4 space-y-5">
          {weddingInfo.faqs.map((faq) => (
            <div key={faq.question}>
              <dt className="font-medium">{faq.question}</dt>
              <dd className="text-ink-soft mt-1">{faq.answer}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section aria-labelledby="contact">
        <SectionHeading id="contact">{weddingInfo.contact.label}</SectionHeading>
        <p className="mt-3">
          <a
            href={`mailto:${weddingInfo.contact.email}`}
            className="text-claret underline decoration-1 underline-offset-4"
          >
            {weddingInfo.contact.email}
          </a>
        </p>
        <p className="mt-1">
          <a
            href={`tel:${weddingInfo.contact.phone.replace(/\s+/g, '')}`}
            className="text-claret underline decoration-1 underline-offset-4"
          >
            {weddingInfo.contact.phone}
          </a>
        </p>
      </section>
    </div>
  )
}

/** Turns a heading into an id, so each section can be labelled by its own text. */
function slug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}
